import React, { useContext, useEffect } from "react";
import { useImmer } from "use-immer";
import {
  createPushNotification,
  deletePushNotificationById,
  getPushNotification,
  getPushNotificationDetails,
  updatePushNotification,
} from "../..";
import { successMessage, errorMessage } from "../../../utils";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../store/AppScopeProvider";
import { TableStateContext } from "../../../store";
import moment from "moment";
import { useDebouncedCallback } from "use-debounce";
import qs from "qs";

export const usePushNotification = ({ isList, isAdd, isEdit }) => {
  const PAGE_NAME = "push_notifications";
  const { appState } = useContext(AppContext);
  const { globalData } = appState;
  const {
    filterState: {
      params: { push_notifications },
    },
    filterStateDispatch,
  } = useContext(TableStateContext);
  const [state, setState] = useImmer({
    isLoading: true,
    isButtonDisable: false,
    dataList: [],
    filterApplied: {
      searchText: "",
      status: "",
    },
    pagination: {
      pageIndex: 1,
      pageSize: 50,
      pageCount: 0,
      total: 0,
      hasMorePages: true,
      lastPage: 1,
    },
    sortBy: [],
    couponList: [],
    deleteModalOpen: false,
    selectedID: null,
    isOpen: false,
    details: {
      id: 1,
      name: "",
      title: "",
      description: "",
      analytics_label: "",
      type: null,
      subject_id: [],
      subject_type: "",
      schedule: "",
      scheduled_date: null,
      scheduled_time: null,
      scheduled_at: "",
      store_wise_timezone: 0,
      timezone: "",
    },
    title: "",
  });
  const navigate = useNavigate();

  const debouncedSearch = useDebouncedCallback((value) => {
    handleGetPushNotificationList({
      searchText: value,
      status: state.filterApplied.status,
      pagination: {
        pageIndex: 1,
        pageSize: state.pagination.pageSize,
      },
      sortBy: state.sortBy,
    });
  }, 1000);

  useEffect(() => {
    if (isList) {
      const { filterApplied, pagination, sortBy } = push_notifications;
      const searchText = filterApplied?.searchText ?? "";
      const status = filterApplied?.status ?? "";
      setState((draft) => {
        draft.filterApplied = {
          searchText,
          status,
        };
        draft.pagination = pagination;
        draft.sortBy = sortBy;
      });
      handleGetPushNotificationList({
        searchText,
        status,
        pagination,
        sortBy,
      });
    }
  }, [isList]);

  useEffect(() => {
    if (isEdit) {
      handleGetPushNotificationDetails(isEdit);
    }
  }, [isEdit]);

  const handleGetPushNotificationList = async ({
    searchText = "",
    status = state.filterApplied.status,
    pagination = state.pagination,
    sortBy = state.sortBy,
  } = {}) => {
    try {
      setState((draft) => {
        draft.isLoading = true;
      });

      let params = {};
      if (searchText) {
        params = { ...params, search: searchText };
      }
      if (status !== "" && status !== null && status !== undefined) {
        params = { ...params, status };
      }
      if (sortBy.length > 0) {
        const sortByParams = sortBy.reduce(
          (obj, item) =>
            Object.assign(obj, { [item.id]: item.desc ? "desc" : "asc" }),
          {},
        );
        params = {
          ...params,
          sort: sortByParams,
        };
      }

      const query = {
        params,
        paramsSerializer: (queryParams) =>
          qs.stringify(queryParams, { encode: true }),
      };

      const res = await getPushNotification(query, pagination);
      if (res.success) {
        const listData = res?.data?.push_notifications ?? res?.data ?? [];
        const paginationData = res?.data?.pagination ?? pagination;

        filterStateDispatch({
          type: "SET_DATA",
          page: PAGE_NAME,
          data: {
            filterApplied: { searchText, status },
            pagination: paginationData,
            sortBy,
          },
        });

        setState((draft) => {
          draft.isLoading = false;
          draft.dataList = listData;
          draft.pagination = paginationData;
          draft.sortBy = sortBy;
          draft.filterApplied.searchText = searchText;
          draft.filterApplied.status = status;
        });
      }
    } catch (err) {
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const handleGetPushNotificationDetails = async (id) => {
    try {
      setState((draft) => {
        draft.isLoading = true;
      });
      const res = await getPushNotificationDetails(id);
      if (res.success) {
        setState((draft) => {
          draft.isLoading = false;
          draft.details = {
            ...res.data,
            scheduled_date: res.data.scheduled_at,
            scheduled_time: moment(
              res.data.scheduled_at,
              "YYYY-MM-DD, hh:mm A",
            ).format("HH:mm:ss"),
            subject_id: res.data.subjects,
          };
          draft.title = res.data.title;
        });
      }
    } catch (err) {
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const onCreatePushNotification = async (status = 1) => {
    try {
      setState((draft) => {
        draft.isLoading = true;
        draft.isButtonDisable = true;
      });

      let formData = new FormData();
      const subjectIds = (state.details.subject_id || []).map(
        (item) => item.id,
      );
      const data = {
        ...state.details,
        store_wise_timezone: state.details.store_wise_timezone ? 1 : 0,
        scheduled_date: moment(state.details.scheduled_date).format(
          "YYYY-MM-DD",
        ),
        scheduled_time: state.details.scheduled_time,
        status: status,
      };
      delete data.subject_id;
      if (data.schedule === 0) {
        delete data.scheduled_date;
        delete data.scheduled_time;
        delete data.timezone;
        delete data.store_wise_timezone;
      }

      Object.keys(data).map((key) => {
        formData.append(key, data[key]);
      });
      subjectIds.forEach((id) => formData.append("subject_id[]", id));
      const res = await createPushNotification(formData);
      if (res.success) {
        successMessage(res.message);

        setState((draft) => {
          draft.isLoading = false;
          draft.isButtonDisable = false;
          draft.isOpen = false;
        });
        navigate("/push-notification");
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      setState((draft) => {
        draft.isLoading = false;
        draft.isButtonDisable = false;
      });
    }
  };

  const onUpdatePushNotification = async (id, status) => {
    try {
      setState((draft) => {
        draft.isLoading = true;
        draft.isButtonDisable = true;
      });

      let formData = new FormData();
      const subjectIds = (state.details.subject_id || []).map(
        (item) => item.id,
      );
      const data = {
        ...state.details,
        store_wise_timezone: state.details.store_wise_timezone ? 1 : 0,
        scheduled_date: moment(state.details.scheduled_date).format(
          "YYYY-MM-DD",
        ),
        scheduled_time: state.details.scheduled_time,
        status: status,
      };
      delete data.subject_id;
      if (data.schedule === 0) {
        delete data.scheduled_date;
        delete data.scheduled_time;
        delete data.timezone;
        delete data.store_wise_timezone;
      }

      Object.keys(data).map((key) => {
        formData.append(key, data[key]);
      });
      subjectIds.forEach((id) => formData.append("subject_id[]", id));
      formData.append("_method", "PUT");
      const res = await updatePushNotification(id, formData);
      if (res.success) {
        successMessage(res.message);

        setState((draft) => {
          draft.isLoading = false;
          draft.isButtonDisable = false;
          draft.isOpen = false;
        });
        navigate("/push-notification");
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      setState((draft) => {
        draft.isLoading = false;
        draft.isButtonDisable = false;
      });
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setState((draft) => {
      draft.details[name] = value;
    });
    if (name === "type") {
      handleChangeCoupons([], null, value === 2 ? "segments" : "coupons");
    }
  };

  const handleReviewModal = (e) => {
    setState((draft) => {
      draft.isOpen = e;
    });
  };

  const handleModal = (v, id = null) => {
    setState((draft) => {
      draft.deleteModalOpen = v;
      draft.selectedID = id;
    });
  };

  const handleDelete = async () => {
    try {
      const res = await deletePushNotificationById(state.selectedID);
      if (res.success) {
        successMessage(res.message);
        setState((draft) => {
          draft.dataList = draft.dataList.filter(
            (item) => item.id !== state.selectedID,
          );
        });
        handleModal(false, null);
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };

  const handleSearch = (value) => {
    setState((draft) => {
      draft.filterApplied.searchText = value;
    });
    debouncedSearch(value);
  };

  const handleStatusChange = (event) => {
    const status = event.target.value;

    setState((draft) => {
      draft.filterApplied.status = status;
    });

    handleGetPushNotificationList({
      searchText: state.filterApplied.searchText,
      status,
      pagination: {
        pageIndex: 1,
        pageSize: state.pagination.pageSize,
      },
      sortBy: state.sortBy,
    });
  };

  const onSearchClear = () => {
    handleGetPushNotificationList({
      searchText: "",
      status: state.filterApplied.status,
      pagination: {
        pageIndex: 1,
        pageSize: state.pagination.pageSize,
      },
      sortBy: state.sortBy,
    });
  };

  const resetFilters = () => {
    if (state.filterApplied.searchText || state.filterApplied.status !== "") {
      handleGetPushNotificationList({
        searchText: "",
        status: "",
        pagination: {
          pageIndex: 1,
          pageSize: state.pagination.pageSize,
        },
        sortBy: state.sortBy,
      });
    }
    setState((draft) => {
      draft.filterApplied.status = "";
      draft.filterApplied.searchText = "";
    });
  };

  const gotoPage = (page) => {
    handleGetPushNotificationList({
      searchText: state.filterApplied.searchText,
      status: state.filterApplied.status,
      pagination: {
        pageIndex: page,
        pageSize: state.pagination.pageSize,
      },
      sortBy: state.sortBy,
    });

    filterStateDispatch({
      type: "UPDATE_PAGE_INDEX",
      page: PAGE_NAME,
      value: page,
    });

    setState((draft) => {
      draft.pagination.pageIndex = page;
    });
  };

  const setPageSize = (e) => {
    const pageSize = +e.target.value;

    handleGetPushNotificationList({
      searchText: state.filterApplied.searchText,
      status: state.filterApplied.status,
      pagination: {
        pageIndex: 1,
        pageSize,
      },
      sortBy: state.sortBy,
    });

    filterStateDispatch({
      type: "UPDATE_PAGE_SIZE",
      page: PAGE_NAME,
      value: pageSize,
    });

    setState((draft) => {
      draft.pagination.pageIndex = 1;
      draft.pagination.pageSize = pageSize;
    });
  };

  const handleSort = (sortBy) => {
    handleGetPushNotificationList({
      searchText: state.filterApplied.searchText,
      status: state.filterApplied.status,
      pagination: state.pagination,
      sortBy,
    });

    filterStateDispatch({
      type: "UPDATE_SORT",
      page: PAGE_NAME,
      value: sortBy,
    });

    setState((draft) => {
      draft.sortBy = sortBy;
    });
  };

  const handleChangeCoupons = (e, v, type) => {
    setState((draft) => {
      if (type === "segments") {
        draft.details.subject_id = (e || []).map((item) => ({
          ...item,
          id: item.value,
        }));
      } else {
        draft.details.subject_id = e ? [{ ...e, id: e.value }] : [];
      }
    });
  };

 const handleRemoveSegments = (id) => {
    setState((draft) => {
      draft.details.subject_id = draft.details.subject_id.filter(
        (item) => item.id !== id
      );
    });
  };

  return {
    state,
    globalData,
    gotoPage,
    setPageSize,
    handleSort,
    handleSearch,
    handleStatusChange,
    onSearchClear,
    resetFilters,
    onChange,
    onUpdatePushNotification,
    onCreatePushNotification,
    handleReviewModal,
    handleModal,
    handleDelete,
    handleChangeCoupons,
    handleRemoveSegments,
  };
};
